import { PageState } from 'models/PageState';
import { BasePageModel } from 'models/BasePageModel';
import { isProductPage, ProductPageModel } from 'models/ProductPageModel';
import { BhImage } from 'models/BhImage';
import {
  IWidget,
  MediaSize,
  MediaWidget,
  WidgetType,
  ListViewProduct
} from 'models/ListViewProduct';
import { IProductMedia } from 'models/IProductMedia';
import { BhVideo } from 'models/BhVideo';
import { BhImageType } from 'models/BhImageType';
import { DetailedProduct } from 'models/DetailedProduct';
import { Price } from 'models/Price';
import { TopSellerBlock } from 'models/TopSellerBlock';
import { RelatedProduct } from 'models/RelatedProduct';
import {
  getConfigurator,
  isInstallationConfigurator,
  isKitchenContactConfigurator,
  isElfaConfigurator,
  isSlidingDoorConfigurator,
  configuratorModifier,
  Nullable,
  isNotForSaleConfigurator
} from './configurators/selector';
import { IQuantity } from 'models/IQuantity';
import { getMediaWidget, splitTitle } from 'components/Shared/Product/selector';
import { createSelector } from 'reselect';
import { ProductDetailedMeta, ProductMeta, Time } from 'models/ProductMeta';
import { SaleStatus } from 'models/SaleStatus';
import { CompareProduct } from 'models/CompareProduct';
import { startOfToday } from 'date-fns';
import { ConfiguratorBase } from 'models/ConfiguratorBase';
import { getProductTagKey } from 'components/Tag';
import { hasElements, withPage } from 'utils';
import { getBrandWidget } from 'components/Shared/Product/selector';
import { IBasicProduct } from 'models/BasicProduct';
import { HeaderState } from 'models/HeaderState';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isDetailedMeta = (meta: any): meta is ProductDetailedMeta =>
  Boolean(meta && 'carriageMethod' in meta);

export type ProductHeaderInfo = Pick<DetailedProduct, 'title' | 'titleSplitPos' | 'variantName'>;
export const getProductHeaderInfo = (currentPage: BasePageModel): ProductHeaderInfo => {
  if (isProductPage(currentPage)) {
    const { title, variantName, titleSplitPos } = currentPage.product;
    return { title, titleSplitPos, variantName };
  }
  return { title: '', variantName: '', titleSplitPos: 0 };
};
export const getProductTitle = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.title : '';

type ProductTitle = Pick<IBasicProduct, 'titleSplitPos' | 'title' | 'ticket' | 'url'>;

export const getProductTitleProps = (state: PageState): ProductTitle | undefined => {
  if (isProductPage(state.currentPage)) {
    const { title, titleSplitPos, url, ticket } = state.currentPage.product;
    return { title, titleSplitPos, url, ticket };
  }
  return;
};

export const getSeriesUrl = ({ currentPage }: PageState) =>
  isProductPage(currentPage) &&
  currentPage.seriesFilter !== undefined &&
  `${currentPage.product.brandUrl}?${currentPage.seriesFilter}`;
export const getProductSeries = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.series : '';
export const getProductBrand = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.brandName : '';
export const getProductTicket = (state: PageState) =>
  isProductPage(state.currentPage) ? state.currentPage.product.ticket : undefined;
export const getProductId = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.productId : '';
export const getVariantId = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.variantId : '';
export const getFullProductId = createSelector(
  getProductId,
  getVariantId,
  (productId, variantId) =>
    variantId === undefined || variantId === '' ? productId : `${productId}-${variantId}`
);
export const getSplashTextMiddle = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.splashTextMiddle : '';
export const getSplashTextEnd = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.splashTextEnd : '';
export const getSplashTextTop = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.splashTextTop : '';
export const getIsFamily = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.isFamily : false;
export const getProductMainWebCatalog = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.mainWebCatalog : '';
export const getIsAddonProduct = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.isAddonProduct : false;
export const getProductIsSaleable = (currentPage: BasePageModel) =>
  isProductPage(currentPage) && !currentPage.product.isAddonProduct;

export const getProductShortDescription = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.shortDescription : {};

export const getProductDescription = ({ currentPage }: PageState) =>
  isProductPage(currentPage) ? currentPage.product.description : undefined;

export const getBundleProductLinks = ({ currentPage }: PageState) =>
  isProductPage(currentPage) ? currentPage.bundleProductLinks : [];

export const getProductPdfs = ({ currentPage }: PageState) =>
  isProductPage(currentPage) ? currentPage.product.productPdfs : [];

export const getEfficiencyLabel = ({ currentPage }: PageState) =>
  isProductPage(currentPage) ? currentPage.product.efficiencyLabel : [];

export const getEfficiencyLabel2020 = ({ currentPage }: PageState) =>
  isProductPage(currentPage) ? currentPage.product.efficiencyLabel2020 : [];

export const getEfficiencyArrow = ({ currentPage }: PageState) =>
  isProductPage(currentPage) ? currentPage.product.efficiencyArrow : undefined;

export const getEfficiencyDeclarationUrl = ({ currentPage }: PageState) =>
  isProductPage(currentPage) ? currentPage.product.efficiencyDeclarationUrl : undefined;

export const getMediaSize = (currentPage: BasePageModel) => {
  const configurator = getConfigurator(currentPage);
  if (isElfaConfigurator(configurator)) {
    return MediaSize.Large;
  }
  if (isProductPage(currentPage)) {
    const media = getMediaWidget(currentPage.product);
    return media ? media.size : MediaSize.Medium;
  }
  return MediaSize.Medium;
};

export const getProductWidgets = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.widgets : [];

export const isImage = (media: IProductMedia): media is BhImage =>
  media.type === BhImageType.Product;
export const isVideo = (media: IProductMedia): media is BhVideo => media.type === BhImageType.Video;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isTopSellers = (topSellers: any): topSellers is TopSellerBlock =>
  Boolean(topSellers && 'products' in topSellers && 'attributes' in topSellers);

export const getAttributes = (currentPage: BasePageModel) => {
  return isProductPage(currentPage) ? currentPage.product.attributes : [];
};
export const getAdminProperties = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.adminProperties : {};
export const getSeriesProducts = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.productsInSeries : [];
export const getOthersAlsoBoughtProducts = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.othersAlsoBoughtProducts : [];

/**
 * Review Selectors
 */

export const getReviewScore = (state: PageState) =>
  isProductPage(state.currentPage) ? state.currentPage.product.reviewAvgScore : 0;

export const getTotalNumberOfReviews = (state: PageState) =>
  isProductPage(state.currentPage)
    ? state.currentPage.product.reviewSummary?.numberOfReviews || 0
    : 0;

export const getReviews = (state: PageState) =>
  isProductPage(state.currentPage) && state.currentPage.reviews ? state.currentPage.reviews : [];

export const getRedReviews = (currentPage: BasePageModel) =>
  isProductPage(currentPage) && currentPage.redReviews
    ? currentPage.redReviews
    : { reviews: [], pageNumber: 1 };

export const getAverageReviewScore = (state: PageState) => {
  return isProductPage(state.currentPage) && state.currentPage.product.reviewSummary !== undefined
    ? state.currentPage.product.reviewSummary.averageScore
    : 0;
};

export const getMostPopularReview = (currentPage: BasePageModel) =>
  isProductPage(currentPage) && currentPage.mostPopularReview
    ? currentPage.mostPopularReview
    : undefined;

export const getRatingsPerLevel = (currentPage: BasePageModel) =>
  isProductPage(currentPage) && currentPage.ratingsPerLevel ? currentPage.ratingsPerLevel : [];

export const getBreadCrumbsStructured = (state: PageState) =>
  isProductPage(state.currentPage) && state.currentPage.breadCrumbsStructured
    ? state.currentPage.breadCrumbsStructured
    : [];
export const getRecommended = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.recommended : [];
export const getIsTopTested = ({ currentPage }: PageState) =>
  isProductPage(currentPage) ? currentPage.product.isTopTested : false;
export const getIsNewProduct = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.isNewProduct : false;
export const getIsCampaign = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.isCampaign : false;
export const getIsPartialCampaign = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.isPartialCampaign : false;
export const getIsBundleSavings = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.isBundleSavings : false;
export const getIsLowPrice = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.isLowPrice : false;
export const getIsLoweredPrice = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.isLoweredPrice : false;

export const getChannel = (state: PageState) => state.appShell.channel;
export const getComponentName = (state: PageState) => state.currentPage.componentName;

export const getProductUrl = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.product.url : '';

export const getNumberOfFaqs = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.totalNumberOfFrequentlyAskedQuestions : 0;

const setVariantPicturesAndAttributes =
  ({ currentPage }: PageState) =>
  (product: CompareProduct) => {
    if (!isProductPage(currentPage) || !isTopSellers(currentPage.topSellers)) {
      return {};
    }

    const productId = getProductId(currentPage);

    if (product.productId !== productId) {
      return product;
    }

    const productImage = getProductImage(currentPage) || product.productImage;

    const attributes = Object.keys(product.attributes).reduce((attributes, key) => {
      const attribute = currentPage.product.attributes.reduce((prev, group) => {
        const property = group.find(({ id }) => id.toLowerCase() === key.toLowerCase());
        return property ? [...prev, property] : prev;
      }, []);

      return {
        ...attributes,
        [key]: hasElements(attribute) ? attribute[0].value : product.attributes[key]
      };
    }, {});

    return {
      ...product,
      url: getProductUrl(currentPage),
      productImage,
      attributes
    };
  };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isCompareProduct = (product: any): product is CompareProduct =>
  Boolean(product && 'productId' in product && 'attributes' in product);

export const getTopSellers = (state: PageState) => {
  const { currentPage } = state;
  if (isProductPage(currentPage) && isTopSellers(currentPage.topSellers)) {
    const { topSellers } = currentPage;
    const products = topSellers.products
      .map(setVariantPicturesAndAttributes(state))
      .filter(isCompareProduct);
    return {
      ...topSellers,
      products
    };
  }
  return undefined;
};

export const getQuantity = (currentPage: BasePageModel): IQuantity =>
  isProductPage(currentPage)
    ? currentPage.product.quantity
    : { amount: 1, highest: 1, lowest: 1, unit: '', salesQuantity: 1 };

export const getProduct = (currentPage: BasePageModel): DetailedProduct =>
  isProductPage(currentPage) ? currentPage.product : ({} as DetailedProduct);

export const getPricePure = ({
  product,
  configurator
}: Pick<ProductPageModel, 'product' | 'configurator'>): Price => {
  const { price } = product;

  // Due to sliding doors showing price per door, but having a min amount of doors
  // constraint, show a lowestPossiblePrice before the customer have chosen a variant
  // and amount of doors for clarification
  const showLowestPriceForConfigurator =
    configurator && (isSlidingDoorConfigurator(configurator) || isElfaConfigurator(configurator));

  if (showLowestPriceForConfigurator) {
    return {
      ...price,
      lowestPossiblePrice: price.listPrice * configuratorModifier(configurator)
    };
  }

  return price;
};

export const getPrice = (currentPage: unknown): Price => {
  if (!isProductPage(currentPage)) {
    return {
      presentationPrice: 0,
      hiddenPrice: false,
      currencyUnit: '',
      isFromPrice: false,
      listPrice: 0,
      lowestPartPaymentPrice: 0
    };
  }

  const { product, configurator } = currentPage;
  return getPricePure({ product, configurator });
};

export const brandWidgetSelector = (state: PageState) =>
  isProductPage(state.currentPage) ? getBrandWidget(state.currentPage.product) : undefined;
export const brandUrlSelector = (state: PageState) =>
  !isProductPage(state.currentPage) ? '' : state.currentPage.product.brandUrl;

export const getBrandUrl = (currentPage: BasePageModel) =>
  !isProductPage(currentPage) ? '' : currentPage.product.brandUrl;
export const getConfiguratorAttributes = (currentPage: BasePageModel) =>
  getConfigurator(currentPage).attributes || [];
export const getConfiguratorVariables = (currentPage: BasePageModel) =>
  getConfigurator(currentPage).values || {};
export const getConfiguratorAttributesPure = (configurator: ConfiguratorBase) =>
  configurator.attributes || [];
export const getConfiguratorVariablesPure = (configurator: ConfiguratorBase) =>
  configurator.values || {};
export const getConfiguratorConstraint = (currentPage: BasePageModel) =>
  getConfigurator(currentPage).constraints || [];

export const isRelatedProduct = (
  relatedProduct: unknown | RelatedProduct | undefined | null
): relatedProduct is RelatedProduct => {
  return (
    typeof relatedProduct === 'object' &&
    (relatedProduct as RelatedProduct).productId !== undefined &&
    (relatedProduct as RelatedProduct).selected !== undefined
  );
};

export const getBundledProduct = (currentPage: BasePageModel) =>
  isProductPage(currentPage) && isRelatedProduct(currentPage.bundledProduct)
    ? currentPage.bundledProduct
    : undefined;

interface AddonProductsSelector {
  name: 'configurator' | 'bundled-product' | 'addons';
  selector: (
    currentPage: BasePageModel
  ) => { price: Price; productId: string; quantity?: IQuantity }[];
}

export const selectedAddonProductSelectors: AddonProductsSelector[] = [
  {
    name: 'configurator',
    selector: currentPage => {
      const configurator = getConfigurator(currentPage);
      return isInstallationConfigurator(configurator)
        ? [
            ...(configurator.installation ? configurator.installation.products : []),
            ...(configurator.addons ? configurator.addons.products : [])
          ].filter(p => p.selected)
        : [];
    }
  },
  {
    name: 'bundled-product',
    selector: currentPage => {
      const bundledProduct = getBundledProduct(currentPage);
      return bundledProduct !== undefined && bundledProduct.selected ? [bundledProduct] : [];
    }
  },
  {
    name: 'addons',
    selector: currentPage => {
      if (!isProductPage(currentPage)) {
        return [];
      }
      const { addons } = currentPage;
      if (addons === undefined) {
        return [];
      }

      return Object.keys(addons)
        .map(key => addons[key].products)
        .reduce((a, b) => a.concat(b), [])
        .filter(p => p.selected);
    }
  }
];

export const getSelectedAddons = (currentPage: BasePageModel) =>
  isProductPage(currentPage)
    ? selectedAddonProductSelectors
        .map(({ selector }) => selector(currentPage))
        .reduce((a, b) => a.concat(b), [])
    : [];

export const getAddonTitles = (state: PageState): string[] => {
  if (!isProductPage(state.currentPage)) {
    return [];
  }

  const addonList = state.currentPage.addons || {};
  const selectedAddonTitles = Object.values(addonList)
    .reduce((previousProducts, addon) => [...previousProducts, ...addon.products], [])
    .filter(product => product.selected)
    .map(({ title }) => title);

  return selectedAddonTitles;
};

export type AddonTotalCost = Required<Pick<Price, 'listPrice' | 'discount'>>;
export const getAddonTotalCost = (currentPage: BasePageModel): AddonTotalCost => {
  if (!isProductPage(currentPage)) {
    return { listPrice: 0, discount: 0 };
  }

  const addons = getSelectedAddons(currentPage);

  return addons.reduce(
    (price, addon) => {
      return {
        listPrice:
          price.listPrice +
          addon.price.presentationPrice * (addon.quantity ? addon.quantity.amount : 1),
        discount: price.discount + (addon.price.discount || 0)
      };
    },
    { listPrice: 0, discount: 0 }
  );
};

export const getDetailedProduct = createSelector(
  [(state: HeaderState) => state.currentPage],
  currentPage => (isProductPage(currentPage) ? currentPage.product : undefined)
);

export const getDetailedMeta = createSelector(
  [getDetailedProduct],
  product => product && (isDetailedMeta(product.meta) ? product.meta : undefined)
);

const deliveryEstimateValues = {
  [Time.TwoToThreeWorkDays]: 3,
  [Time.TwoToFiveWorkDays]: 5,
  [Time.ThreeToFiveWorkDays]: 5,
  [Time.FiveToTenWorkDays]: 10,
  [Time.TwoToFourWeeks]: 20,
  [Time.FourToSixWeeks]: 30,
  [Time.SixToEightWeeks]: 40,
  [Time.EightToTenWeeks]: 50,
  [Time.TenToTwelveWeeks]: 60,
  [Time.TwelveToFourteenWeeks]: 70,
  [Time.FourteenToSixteenWeeks]: 80,
  [Time.TwoToThreeWeeks]: 15,
  [Time.ThreeToFourWeeks]: 20,
  [Time.FourToSevenWorkDays]: 7,
  [Time.NotDefined]: 0
} as { [time: number]: number };

const deliveryEstimateValue = (t: Time): number => deliveryEstimateValues[t] || 0;

export const getStockMeta = createSelector(
  [getDetailedProduct, state => getSelectedAddons(state.currentPage)],
  (product, selectedAddons) => {
    if (product === undefined || !isDetailedMeta(product.meta)) {
      return undefined;
    }

    const addonsMeta = selectedAddons
      .filter(isRelatedProduct)
      .filter(addon => addon.meta !== undefined)
      .map(addon => addon.meta)
      .filter(isDetailedMeta);

    const metas = [product.meta, ...addonsMeta];

    const [carriage] = metas.sort((a, b) => a.carriageMethod - b.carriageMethod);
    const [delivery] = metas.sort(
      (a, b) =>
        deliveryEstimateValue(b.deliveryEstimate) - deliveryEstimateValue(a.deliveryEstimate)
    );
    const [deliveryCostAmount] = metas.sort((a, b) => b.deliveryCostAmount - a.deliveryCostAmount);

    return {
      ...delivery,
      deliveryCost: deliveryCostAmount.deliveryCost,
      carriageMethod: carriage.carriageMethod
    };
  }
);

export const isMediaWidget = (widget: IWidget): widget is MediaWidget =>
  widget.type === WidgetType.Media;

export const getProductImage = (currentPage: BasePageModel) => {
  if (!isProductPage(currentPage)) {
    return undefined;
  }

  const widget = currentPage.product.widgets?.find(isMediaWidget);

  if (widget === undefined || !Array.isArray(widget.media)) {
    return undefined;
  }

  return widget.media.find(isImage);
};

export const isInactivePure = (product: ListViewProduct | undefined) =>
  product && product.meta?.saleStatus === SaleStatus.InActive;
export const isInactive = createSelector(getDetailedProduct, isInactivePure);

const getMeta = createSelector(getDetailedProduct, product => product && product.meta);
export const isInStockPure = (meta?: ProductMeta) => {
  if (!meta) return false;
  const saleDate = new Date(meta.publishedSaleDate);
  const today = startOfToday();

  if (meta.saleStatus === SaleStatus.InStock && saleDate > today) {
    return false;
  }

  return meta.saleStatus === SaleStatus.Active || meta.saleStatus === SaleStatus.InStock;
};
export const isBackorderPure = (meta?: ProductMeta) => {
  return meta ? meta.isBackOrder : false;
};
export const isInStock = createSelector(getMeta, isInStockPure);
export const isBackorder = createSelector(getMeta, isBackorderPure);

export const showDeliveryInfo = createSelector(
  getConfigurator,
  config => !isKitchenContactConfigurator(config) && !isNotForSaleConfigurator(config)
);

export const showCompare = createSelector(
  getConfigurator,
  config => !isKitchenContactConfigurator(config) && !isNotForSaleConfigurator(config)
);

export const showSavings = createSelector(
  getConfigurator,
  config => !isKitchenContactConfigurator(config) && !isNotForSaleConfigurator(config)
);

export const isNonPurchaseableConfiguratorPure = isKitchenContactConfigurator;

const isNonPurchaseableConfigurator = createSelector(
  [(state: HeaderState) => state.currentPage],
  currentPage =>
    isKitchenContactConfigurator(getConfigurator(currentPage)) ||
    isNotForSaleConfigurator(getConfigurator(currentPage))
);

export const isAddonProduct = createSelector(
  getDetailedProduct,
  product => product && product.isAddonProduct
);

const getIsPurchasable = (
  isInStock: boolean,
  isNonPurchaseableConfigurator: boolean,
  isBackorder?: boolean,
  shouldDisableBackOrders?: boolean,
  isInactive?: boolean
) => {
  if (isInactive || isNonPurchaseableConfigurator) {
    return false;
  }

  if (shouldDisableBackOrders) {
    return isInStock && !isBackorder;
  }

  return isInStock || !!isBackorder;
};

export const isPurchasablePure = (
  product: ListViewProduct,
  configurator?: Nullable<ConfiguratorBase>,
  meta?: ProductMeta,
  shouldDisableBackOrders?: boolean
) => {
  const isNonPurchasableConfigurator = configurator
    ? isNonPurchaseableConfiguratorPure(configurator)
    : false;
  const isInStock = isInStockPure(meta);
  const isBackorder = isBackorderPure(meta);
  const isInactive = isInactivePure(product);

  return getIsPurchasable(
    isInStock,
    isNonPurchasableConfigurator,
    isBackorder,
    shouldDisableBackOrders,
    isInactive
  );
};

export const getShouldDisableBackOrders = (state: PageState): boolean =>
  state.appShell.shouldDisableBackOrders;

export const isPurchasable = createSelector(
  isInStock,
  isInactive,
  isNonPurchaseableConfigurator,
  isBackorder,
  getShouldDisableBackOrders,
  (isInStock, isInactive, isNonPurchaseableConfigurator, isBackorder, shouldDisableBackOrders) => {
    return getIsPurchasable(
      isInStock,
      isNonPurchaseableConfigurator,
      isBackorder,
      shouldDisableBackOrders,
      isInactive
    );
  }
);

export const showDoorPrice = (currentPage: unknown): boolean =>
  isProductPage(currentPage) && configuratorModifier(currentPage.configurator) > 1;

export const productIdSelector = (state: PageState) => getProductId(state.currentPage);

export const productTagSelector = (state: PageState) => {
  const isTopTested = getIsTopTested(state);
  const isNewProduct = getIsNewProduct(state.currentPage);

  return getProductTagKey({ isNewProduct, isTopTested });
};

export const productUsps = (state: PageState) =>
  isProductPage(state.currentPage) ? state.currentPage.product.usps : [];

export const productInfoSelector = (state: PageState) => {
  const { variantName, ...titleProps } = getProductHeaderInfo(state.currentPage);
  const { title, category } = splitTitle(titleProps);

  return { title, variantName, category };
};

export const siteModeSelector = (state: PageState) => {
  return state.appShell.siteMode;
};

export const topContentAreaSelector = (state: PageState) => {
  return isProductPage(state.currentPage) ? state.currentPage.topContentArea : undefined;
};

export const getEnticement = withPage(isProductPage, (page: ProductPageModel) => page.enticement);

export const getPartPaymentPrice = withPage(
  isProductPage,
  (page: ProductPageModel) => page.product.price.lowestPartPaymentPrice
);

export const selectTacticalFlagMessage = withPage(
  isProductPage,
  (page: ProductPageModel) => page.product.tacticalFlagMarking
);

export const selectTacticalFlagColor = withPage(
  isProductPage,
  (page: ProductPageModel) => page.product.tacticalFlagColor
);

export const selectShouldUseNotifyMe = withPage(
  isProductPage,
  (page: ProductPageModel) => page.shouldUseNotifyMe
);

export const selectPriceGuarantee = withPage(
  isProductPage,
  (page: ProductPageModel) => page.priceGuarantee
);

export const selectRecommendedPrice = withPage(
  isProductPage,
  (page: ProductPageModel) => page.product.price.recommendedPriceString
);

export const selectPartPayment = withPage(
  isProductPage,
  (page: ProductPageModel) => page.product.partPayments
);
